import React, { useEffect, useState, useCallback,useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineExclamation,
  HiOutlineX,
  HiOutlineChevronLeft,
  HiOutlinePhotograph,
  HiOutlineClock,
  HiOutlineClipboardList,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineUpload,
  HiOutlineDownload,
  HiOutlineDocumentText,
} from "react-icons/hi";
import {
  fetchCompetencies,
  fetchAllSets,
  fetchAllSetsLite,
  createSet,
  updateSet,
  deleteSet,
  fetchQuestionsForSet,
  uploadSetResource,
  fetchMasterData,
  fetchStateById,
  fetchSet,
  bulkUpload,
  bulkUploadMcqSet,
  fetchCompetency,
} from "../services/masterDataApi";
import { downloadXlsxMultiSheet } from "../services/xlsxTemplate";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import FilterDropdown from "../components/FilterDropdown";
import SearchableDropdown, { scopedStatesFetchFn } from "../components/SearchableDropdown";
import TiptapEditor from "../components/TiptapEditor";
import { useUserRole } from "../hooks/useUserRole";
import { fetchMyStates } from "../services/assignmentApi";
import { useDebounce } from "../hooks/useDebounce";

/* Mirrors sloutils.rich_text.is_blank_rich_text — a required field is defeated
   by an empty string, by whitespace, or by markup that renders to nothing
   ("<p></p>" is what an emptied editor produces, and "&nbsp;" survives trim).
   An embedded image counts as content, so a picture-only question is allowed. */
const isBlankText = (value) => {
  if (!value) return true;
  if (/<(?:img|video|audio|iframe)\b/i.test(value)) return false;
  return !value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
    .trim();
};

/* ── style tokens ─────────────────────────────────────────────── */
const inputClass =
  "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4]";
const selectClass =
  "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4]";
const labelClass = "block text-sm font-medium text-[#24292f] mb-1";
// <input type="number"> still lets the browser accept e/E/+/- as valid
// keystrokes (scientific-notation syntax) even for a plain integer field —
// block them at the keyboard so only digits can be typed.
const blockNonNumericKeys = (e) => {
  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
};
// The HTML `max` attribute only stops the up/down spinner and arrow keys —
// typing a value directly (or pasting) sails right past it. Clamp in the
// change handler too so a typed 10000000 can't slip through into state.
const clampNumber = (val, min, max) => {
  if (val === "" || val === null || Number.isNaN(val)) return null;
  return Math.min(max, Math.max(min, val));
};
const btnPrimary =
  "flex items-center gap-1.5 px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] transition-all";
const btnSecondary =
  "px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] transition-all";

const SET_TYPES = [
  { value: "mcq", label: "MCQ", desc: "Multiple-choice questions only", color: "blue" },
  { value: "grid", label: "Grid", desc: "Grid reading assessment only", color: "emerald" },
];

/* ── bulk upload modes ── */
// One upload per set type, each with its OWN template carrying only the columns
// relevant to that type — no grid fields on an mcq upload or vice versa, and the
// backend rejects the other type's rows outright.
//
// The MCQ workbook holds both sheets: "Sets" for the sets and "Questions" for
// their questions, joined on (competency_code, state, set_title) — there is no
// set id in the file. `state` is the state name; it may be left blank while the
// competency code exists in only one state, but is required once the same code
// exists in several (a code is unique per state, not globally). One call creates both, all-or-nothing. Leaving the "Sets" sheet
// empty (headers only) adds questions to sets that already exist, which is what
// the old separate "Upload MCQs" button did.
const UPLOAD_MODES = {
  mcq: {
    title: "Bulk Upload MCQ-Sets",
    hint: "Upload an Excel file (.xlsx) to create MCQ sets with their questions",
    download: () => downloadMcqSetTemplate(),
    apiFn: (file) => bulkUploadMcqSet(file),
  },
  grid: {
    title: "Bulk Upload Grid-Sets",
    hint: "Upload an Excel file (.xlsx) to create Grid sets",
    download: () => downloadGridTemplate(),
    apiFn: (file) => bulkUpload("competency-sets", file),
  },
};

/* ── empty question template ── */
const emptyQuestion = () => ({
  _key: Date.now() + Math.random(),
  text: "",
  answer: "",
  image_url: "",
  order: 0,
  options: [
    { text: "Correct", is_correct: true },
    { text: "Wrong", is_correct: false },
    { text: "Skipped", is_correct: false },
  ],
  min_grid_score_required: null,
});

/* ── bulk upload templates ── */
// The sheet NAMES matter: the backend looks up "Sets" / "Questions" by name
// (unlike every other bulk upload, which reads the active sheet), so these go
// through downloadXlsxMultiSheet rather than downloadXlsxTemplate — the latter
// always names its single sheet "Template", which the endpoints reject.

// mcq-only columns — no grid fields (is_timed/duration_secs/grid_text/
// feedback_*/passing_score) shown at all. Second sheet carries the questions,
// joined to the first on (competency_code, state, set_title).
function downloadMcqSetTemplate() {
  const setHeaders = [
    "competency_code", "state", "title", "set_type", "description", "instructions", "order",
    "thresholds_enabled", "passing_percentage",
    "stop_rule_enabled", "stop_if_consec",
    "dependent_set_title", "dependent_set_competency_code", "dependent_set_state", "resource_urls",
  ];
  const setRows = [
    // (a) plain mcq set
    ["C101", "Bihar", "Addition MCQ", "mcq", "Basic addition questions", "Answer all questions", "1",
      "true", "60",
      "false", "",
      "", "", "", "https://res.example/s1"],
    // (b) mcq set depending on another set — dependent_set_title is that set's
    // exact title, and dependent_set_competency_code is always required
    // alongside it to disambiguate (titles aren't unique across the DB). This
    // works identically whether the target set is in THIS file or was created
    // in a separate, earlier upload (e.g. via "Upload Grid-Sets").
    // dependent_set_state defaults to the row's own state and must match it.
    ["C101", "Bihar", "Follow-up MCQ", "mcq", "Follow-up questions after grid", "Answer all questions", "2",
      "true", "70",
      "false", "",
      "Number Grid", "C101", "", ""],
  ];
  const questionHeaders = [
    "competency_code", "state", "set_title", "question_text", "description", "order", "image_url",
    "option_1", "option_2", "option_3", "option_4", "correct_option", "min_grid_score_required",
  ];
  // set_title matches a title in the Sets sheet above — or a set already in the
  // database, in which case the Sets sheet can be left as headers only.
  const questionRows = [
    ["C101", "Bihar", "Addition MCQ", "What is 2 + 3?", "", "1", "", "5", "6", "4", "", "1", ""],
    ["C101", "Bihar", "Addition MCQ", "What is 4 + 4?", "", "2", "", "7", "8", "9", "", "2", ""],
    ["C101", "Bihar", "Follow-up MCQ", "What is 5 + 5?", "", "1", "", "10", "11", "9", "", "1", ""],
  ];
  downloadXlsxMultiSheet("mcq_set_template.xlsx", [
    { name: "Sets", headers: setHeaders, sampleRows: setRows },
    { name: "Questions", headers: questionHeaders, sampleRows: questionRows },
  ]);
}

// grid-only columns — no mcq fields (passing_percentage) shown at all;
// includes the grid builder (grid_text) AND the feedback question that can
// follow a grid (feedback_text/feedback_option_1-4/feedback_correct_option) —
// this was already part of grid sets before the upload split and still is.
function downloadGridTemplate() {
  const headers = [
    "competency_code", "state", "title", "set_type", "description", "instructions", "order",
    "thresholds_enabled", "passing_score", "is_timed", "duration_secs",
    "stop_rule_enabled", "stop_if_consec", "grid_text", "feedback_text",
    "feedback_option_1", "feedback_option_2", "feedback_option_3", "feedback_option_4", "feedback_correct_option",
    "dependent_set_title", "dependent_set_competency_code", "dependent_set_state", "resource_urls",
  ];
  const sampleRows = [
    ["C101", "Bihar", "Number Grid", "grid", "Read the number grid", "Read aloud each number", "1",
      "false", "20", "true", "60",
      "true", "3", "12 34 56 78 90", "Great reading!",
      "Fluent", "Needs practice", "Struggling", "", "1",
      "", "", "", ""],
  ];
  downloadXlsxMultiSheet("grid_sets_template.xlsx", [
    { name: "Sets", headers, sampleRows },
  ]);
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
function Sets() {
  const { canManageCompetencies, role } = useUserRole();
  const [toast, setToast] = useState(null);

  /* ── views: "list" | "form" — backed by the URL so a refresh in edit mode
     reopens the same set instead of dropping back to the list ── */
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "list";
  const urlEditId = searchParams.get("edit");
  const setView = useCallback((v, params = {}) => {
    if (v === "list") {
      setSearchParams({});
    } else if (v === "form" && params.edit) {
      setSearchParams({ view: "form", edit: params.edit });
    } else {
      setSearchParams({ view: v });
    }
  }, [setSearchParams]);

  /* ── selected competency (resolved by code, not from a paged list) ── */
  const [selectedCompetency, setSelectedCompetency] = useState(null);

  /* ── sets state ── */
  const [sets, setSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  /* ── lightweight full list of all accessible sets (for cross-set dependency lookups) ── */
  const [allSetsLite, setAllSetsLite] = useState([]);

  /* ── pagination state ── */
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  /* ── list filters: State -> Competency (scoped to that state) -> Set Type ── */
  const emptySetFilters = { state_id: "", competency_code: "", set_type: "" };
  const [listFilters, setListFilters] = useState(emptySetFilters);
  const [draftListFilters, setDraftListFilters] = useState(emptySetFilters);
  // Full competency list scoped to whichever state is selected in the Filters panel,
  // fetched on demand and cached by state_id — same pattern as Schools.js's
  // filterDistrictsByState (loops past the server's page_size=100 cap).
  const [filterCompetenciesByState, setFilterCompetenciesByState] = useState({});
  // Unscoped fallback for the Competency filter when State is left on "All" —
  // without this the dropdown showed nothing at all until a state was picked.
  const [allCompetenciesForFilter, setAllCompetenciesForFilter] = useState(null);

  /* ── set form ── */
  const [setForm, setSetForm] = useState(null);
  const [editingSetId, setEditingSetId] = useState(null);
  const [saving, setSaving] = useState(false);
  /* ── inline questions (managed as part of the form) ── */
  const [formQuestions, setFormQuestions] = useState([]);
  const [expandedQuestionIdx, setExpandedQuestionIdx] = useState(null);

  /* ── state mapping ── */
  const [states, setStates] = useState([]);

  /* ── delete confirm ── */
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  /* ── bulk upload ── */
  const fileInputRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState("mcq"); // "mcq" | "grid"
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  /* ── loaders ── */
  // Non-ADMIN users load their own assigned state(s). (GET /commons/list?type=states
  // is now role-scoped too, so this gives the same result.) Only ADMIN and SPL
  // reach this form at all (WRITE_ROLES = {ADMIN, SPL}).
  const loadStates = useCallback(async () => {
    if (!role) return;
    try {
      let list = [];
      if (role === "ADMIN") {
        const res = await fetchMasterData("states", { page: 1, page_size: 100 });
        list = res.success ? (res.data || []) : [];
      } else {
        const res = await fetchMyStates();
        list = res?.data || res || [];
      }
      setStates(list);
      // One accessible state means there is nothing to choose — preselect it.
      // Keyed off the list length, not the role name, so it stays correct if
      // another role is granted set access later.
      if (list.length === 1) {
        setSetForm((prev) =>
          prev && !prev.state_id ? { ...prev, state_id: String(list[0].id) } : prev
        );
      }
    } catch (err) {
      console.error("Failed to load states", err);
      setStates([]);
    }
  }, [role]);

  const loadSets = useCallback(async (page = currentPage, search = debouncedSearchTerm, activeFilters = listFilters) => {
    setSetsLoading(true);
    try {
      const res = await fetchAllSets({
        page,
        page_size: pageSize,
        search,
        state_id: activeFilters.state_id || undefined,
        competency_code: activeFilters.competency_code || undefined,
        set_type: activeFilters.set_type || undefined,
      });
      setSets(res.data || []);
      setTotalCount(res.total_count || 0);
      setTotalPages(res.total_pages || 1);
      setCurrentPage(res.current_page || page);
    } catch (err) {
      console.error(err);
      setSets([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setSetsLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, pageSize, listFilters]);

  // Lightweight full list of all accessible sets — used for cross-set dependency selection
  // so it isn't limited to whatever page of `sets` happens to be loaded.
  const loadAllSetsLite = useCallback(async () => {
    try {
      const data = await fetchAllSetsLite();
      setAllSetsLite(data || []);
    } catch (err) {
      console.error("Failed to load lite sets list", err);
      setAllSetsLite([]);
    }
  }, []);

  useEffect(() => { loadStates(); }, [loadStates]);
  useEffect(() => { loadAllSetsLite(); }, [loadAllSetsLite]);

  useEffect(() => {
    setCurrentPage(1);
    loadSets(1, debouncedSearchTerm, listFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, listFilters, pageSize]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Fetch the full competency list for whichever state is selected in the sets
  // Filters panel (loops past the server's page_size=100 cap) — same pattern as
  // Schools.js's filterDistrictsByState.
  useEffect(() => {
    const stateId = draftListFilters.state_id;
    if (!stateId || filterCompetenciesByState[stateId]) return;
    let cancelled = false;
    (async () => {
      let page = 1;
      let all = [];
      while (true) {
        const res = await fetchCompetencies({ page, page_size: 100, search: "", state_id: stateId });
        all = all.concat(res.data || []);
        if (cancelled || page >= (res.total_pages || 1)) break;
        page += 1;
      }
      if (!cancelled) {
        setFilterCompetenciesByState((prev) => ({ ...prev, [stateId]: all }));
      }
    })();
    return () => { cancelled = true; };
  }, [draftListFilters.state_id, filterCompetenciesByState]);

  // Unscoped competency list for the Filters panel when State is "All" —
  // fetched once, lazily, the first time the Filters panel is opened (it is
  // a full page-by-page loop, and nothing outside the panel uses it).
  const [filterPanelOpened, setFilterPanelOpened] = useState(false);
  useEffect(() => {
    if (!filterPanelOpened) return;
    if (draftListFilters.state_id || allCompetenciesForFilter !== null) return;
    let cancelled = false;
    (async () => {
      let page = 1;
      let all = [];
      while (true) {
        const res = await fetchCompetencies({ page, page_size: 100, search: "" });
        all = all.concat(res.data || []);
        if (cancelled || page >= (res.total_pages || 1)) break;
        page += 1;
      }
      if (!cancelled) setAllCompetenciesForFilter(all);
    })();
    return () => { cancelled = true; };
  }, [filterPanelOpened, draftListFilters.state_id, allCompetenciesForFilter]);

  // Resolve the selected competency by code instead of searching a loaded list.
  // The old competencies.find() searched a single page_size:100 fetch, so any
  // competency past the first 100 silently missed: the State box stayed blank and
  // the Dependency Set section disappeared. Covers both entry points — picking
  // one in the create form and opening a set for edit — since they both set
  // setForm.competency_code.
  // Keyed on the code we last *attempted*, not on the resolved object: keying on
  // the result meant a failed fetch cleared the object, which re-ran the effect
  // and refetched the same code forever.
  const attemptedCompetencyRef = useRef(null);
  useEffect(() => {
    const code = setForm?.competency_code;
    if (!code) {
      attemptedCompetencyRef.current = null;
      setSelectedCompetency(null);
      return;
    }
    if (attemptedCompetencyRef.current === code) return;
    attemptedCompetencyRef.current = code;
    let cancelled = false;
    fetchCompetency(code)
      .then((comp) => { if (!cancelled) setSelectedCompetency(comp || null); })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to resolve competency", code, err);
        setSelectedCompetency(null);
      });
    return () => { cancelled = true; };
  }, [setForm?.competency_code]);

  // Mirror the resolved competency's state into the form the first time it lands,
  // so opening an existing set shows the right state and scopes the competency
  // list to it. Never overrides a state the user picked.
  useEffect(() => {
    if (!selectedCompetency || selectedCompetency.code !== setForm?.competency_code) return;
    if (setForm?.state_id) return;
    const sid = selectedCompetency.state_id ? String(selectedCompetency.state_id) : "";
    if (sid) updateSetForm({ state_id: sid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompetency, setForm?.competency_code, setForm?.state_id]);

  /* ══════════════════════════════════════════════════════════════
     SET CRUD
     ══════════════════════════════════════════════════════════════ */
  const defaultForm = () => ({
    title: "",
    set_type: "mcq",
    description: "",
    instructions: "",
    introduction: "",
    resources: [],
    state_id: states.length === 1 ? String(states[0].id) : "",
    competency_code: "",
    is_timed: false,
    duration_secs: null,
    stop_rule_enabled: false,
    stop_if_consec: null,
    thresholds_enabled: false,
    passing_percentage: null,
    passing_score: null,
    grid_text: "",
    grid_items: [],
    feedback_question: null,
    dependent_set_id: null,
  });

  // Helper to update form fields
  const updateSetForm = (updates) => {
    setSetForm((prev) => ({ ...prev, ...updates }));
  };

  const openSetCreate = () => {
    setSetForm(defaultForm());
    setFormQuestions([]);
    setEditingSetId(null);
    setExpandedQuestionIdx(null);
    setView("form");
  };

  const openSetEdit = async (s) => {
    // A direct click already has the full item — mark the URL-restore effect
    // as satisfied so it doesn't also redundantly re-fetch this same set
    // once setView() below updates the URL.
    hasRestoredFromUrl.current = true;
    setSetForm({
      title: s.title || "",
      set_type: s.set_type || "mcq",
      description: s.description || "",
      instructions: s.instructions || "",
      introduction: s.introduction || "",
      resources: s.resources || [],
      competency_code: s.competency_code,
      // Seed the owning competency's state from the set itself: resolving it
      // from the code alone would pick an arbitrary state when the code exists
      // in several, and saving would then move the set there.
      state_id: s.competency_state_id ? String(s.competency_state_id) : "",
      is_timed: !!s.is_timed,
      duration_secs: s.duration_secs || null,
      stop_rule_enabled: !!s.stop_rule_enabled,
      stop_if_consec: s.stop_if_consec || null,
      thresholds_enabled: !!s.thresholds_enabled,
      passing_percentage: s.passing_percentage || null,
      passing_score: s.passing_score || null,
      grid_text: s.grid_text || "",
      grid_items: s.grid_items || [],
      feedback_question: s.feedback_question || null,
      dependent_set_id: s.dependent_set_id || null,
    });
    setEditingSetId(s.id);
    setView("form", { edit: s.id });
    setExpandedQuestionIdx(null);

    // Load existing questions
    try {
      const qs = await fetchQuestionsForSet(s.id);
      setFormQuestions(qs.map((q) => ({ ...q, _key: q.id || Date.now() + Math.random(), answer: q.answer || q.description || "" })));
    } catch {
      setFormQuestions([]);
    }
  };

  // Restore edit view from URL on refresh — the set list only has a subset of
  // fields, so re-fetch the full set (same endpoint used elsewhere) and feed
  // it through the normal openSetEdit path.
  const hasRestoredFromUrl = useRef(false);
  useEffect(() => {
    if (hasRestoredFromUrl.current) return;
    if (view === "form" && urlEditId) {
      hasRestoredFromUrl.current = true;
      fetchSet(urlEditId)
        .then((res) => openSetEdit(res))
        .catch(() => setView("list"));
    } else if (view === "form" && !urlEditId) {
      hasRestoredFromUrl.current = true;
      openSetCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, urlEditId]);

  const handleSetSave = async () => {
    if (!setForm.title.trim()) {
      setToast({ type: "error", message: "Title is required" });
      return;
    }
    if (!setForm.competency_code) {
      setToast({ type: "error", message: "Please select a competency" });
      return;
    }
    // Question Text is marked required in the form but was never checked here,
    // so a question with no text saved silently. Point at the offending one by
    // number rather than just refusing.
    const blankQuestionIdx = formQuestions.findIndex((q) => isBlankText(q.text));
    if (blankQuestionIdx !== -1) {
      setExpandedQuestionIdx(blankQuestionIdx);
      setToast({
        type: "error",
        message: `Question ${blankQuestionIdx + 1} needs question text`,
      });
      return;
    }
    if (setForm.is_timed && !(parseInt(setForm.duration_secs, 10) > 0)) {
      setToast({ type: "error", message: "Duration (seconds) is required when Timed is enabled" });
      return;
    }
    if (setForm.stop_rule_enabled && !(parseInt(setForm.stop_if_consec, 10) > 0)) {
      setToast({ type: "error", message: "Consecutive wrong answers count is required when Stop Rule is enabled" });
      return;
    }
    if (setForm.thresholds_enabled) {
      if (setForm.set_type === "mcq" && !(parseFloat(setForm.passing_percentage) > 0)) {
        setToast({ type: "error", message: "Passing percentage is required when Thresholds is enabled" });
        return;
      }
      if (setForm.set_type === "grid" && !(parseInt(setForm.passing_score, 10) > 0)) {
        setToast({ type: "error", message: "Passing score is required when Thresholds is enabled" });
        return;
      }
    }
    setSaving(true);
    try {
      const validResources = (setForm.resources || []).filter((r) => r.url && r.url.trim());

      // Build questions payload (strip _key; keep id so an existing
      // question is updated in place instead of recreated with a new id)
      const questionsPayload = formQuestions.map((q, idx) => ({
        id: q.id || undefined,
        text: q.text,
        description: q.answer || q.description || "",
        image_url: q.image_url || null,
        order: idx,
        options: q.options || [],
        min_grid_score_required: q.min_grid_score_required || null,
      }));

      const payload = {
        title: setForm.title,
        set_type: setForm.set_type,
        description: setForm.description || null,
        instructions: setForm.instructions || null,
        introduction: setForm.introduction || null,
        resources: validResources,
        is_timed: setForm.is_timed,
        duration_secs: setForm.is_timed ? (setForm.duration_secs || null) : null,
        stop_rule_enabled: setForm.stop_rule_enabled,
        stop_if_consec: setForm.stop_rule_enabled ? (setForm.stop_if_consec || null) : null,
        thresholds_enabled: setForm.thresholds_enabled,
        passing_percentage: setForm.thresholds_enabled ? (setForm.passing_percentage || null) : null,
        passing_score: setForm.thresholds_enabled ? (setForm.passing_score || null) : null,
        grid_text: setForm.grid_text || null,
        grid_items: setForm.grid_items || [],
        feedback_question: setForm.set_type === "grid" ? (setForm.feedback_question || null) : null,
        dependent_set_id: setForm.set_type === "mcq" ? (setForm.dependent_set_id || null) : null,
        questions: questionsPayload,
        // Sent on update too: update_set re-parents the HAS_SET edge when this
        // differs from the set's current owner. state_id picks which state's
        // competency is meant — a code is only unique per state.
        competency_code: setForm.competency_code,
        state_id: setForm.state_id ? Number(setForm.state_id) : null,
      };

      if (editingSetId) {
        await updateSet(editingSetId, payload);
        setToast({ type: "success", message: "Set updated" });
        setView("list");
        setSetForm(null);
        setFormQuestions([]);
        loadSets();
        loadAllSetsLite();
      } else {
        await createSet(payload);
        setToast({ type: "success", message: "Set created" });
        setView("list");
        setSetForm(null);
        setFormQuestions([]);
        loadSets();
        loadAllSetsLite();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg || d).join(", ") : detail || "Failed to save set";
      setToast({ type: "error", message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleSetDelete = async (id) => {
    try {
      await deleteSet(id);
      setDeleteConfirm(null);
      setToast({ type: "success", message: "Set deleted" });
      if (view !== "list") {
        setSetForm(null);
        setFormQuestions([]);
        setView("list");
      }
      loadSets();
      loadAllSetsLite();
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.detail || "Failed to delete" });
    }
  };

  /* ── resource handling (image-only) ── */
  const [resourceUploading, setResourceUploading] = useState(false);
  const handleResourceUpload = async (file) => {
    if (!file) return;
    setResourceUploading(true);
    try {
      const data = await uploadSetResource(file);
      updateSetForm({
        resources: [...(setForm.resources || []), { url: data.url, file_type: "image", file_size: data.file_size, label: data.filename }],
      });
      setToast({ type: "success", message: "Image uploaded" });
    } catch (err) {
      setToast({ type: "error", message: "Image upload failed" });
    } finally {
      setResourceUploading(false);
    }
  };
  const removeResource = (idx) => {
    updateSetForm({ resources: setForm.resources.filter((_, i) => i !== idx) });
  };

  /* ══════════════════════════════════════════════════════════════
     INLINE QUESTION MANAGEMENT
     ══════════════════════════════════════════════════════════════ */
  const addQuestion = () => {
    const q = emptyQuestion();
    q.order = formQuestions.length;
    setFormQuestions([...formQuestions, q]);
    setExpandedQuestionIdx(formQuestions.length);
  };
  const removeQuestion = (idx) => {
    setFormQuestions(formQuestions.filter((_, i) => i !== idx));
    setExpandedQuestionIdx(null);
  };
  const updateQuestion_local = (idx, field, value) => {
    const qs = [...formQuestions];
    qs[idx] = { ...qs[idx], [field]: value };
    setFormQuestions(qs);
  };
  const addOption = (qIdx) => {
    const qs = [...formQuestions];
    qs[qIdx] = { ...qs[qIdx], options: [...qs[qIdx].options, { text: "", is_correct: false }] };
    setFormQuestions(qs);
  };
  const removeOption = (qIdx, oIdx) => {
    const qs = [...formQuestions];
    qs[qIdx] = { ...qs[qIdx], options: qs[qIdx].options.filter((_, i) => i !== oIdx) };
    setFormQuestions(qs);
  };
  const updateOption = (qIdx, oIdx, field, value) => {
    const qs = [...formQuestions];
    const opts = [...qs[qIdx].options];
    opts[oIdx] = { ...opts[oIdx], [field]: value };
    qs[qIdx] = { ...qs[qIdx], options: opts };
    setFormQuestions(qs);
  };

  /* ── Grid Items builder ── */
  const handleGridTextChange = (text) => {
    const words = text.split(/\s+/).filter(Boolean);
    updateSetForm({ grid_text: text, grid_items: words.map((w) => ({ text: w })) });
  };

  /* ── helpers ── */
  const hasGrid = setForm && setForm.set_type === "grid";
  const hasMcq = setForm && setForm.set_type === "mcq";

  /* ── competency options: server-side search + paging (20 at a time) ──
     Replaces a single page_size:100 fetch, which could not reach competency 101+
     because the endpoint hard-caps page_size at 100. */
  // Competencies are listed for the chosen state. Left unscoped when no state is
  // set: the backend filters `c.state_id = $state_id`, which would also exclude
  // the rows that carry no state at all.
  const competencyStateId = setForm?.state_id ? Number(setForm.state_id) : undefined;
  const competencyFetchFn = (search, page = 1) =>
    fetchCompetencies({ search, page, page_size: 20, state_id: competencyStateId }).then((res) => {
      // Competencies are code-keyed — there is no `id` field.
      const options = (res.data || []).map((c) => ({
        id: c.code,
        name: `${c.code} - ${c.name}`,
      }));
      // SearchableDropdown resolves a preselected label by scanning page 1 only, so
      // an existing set whose competency sorts later would show an empty box. Put
      // the current selection on page 1 so it always resolves and stays visible.
      if (
        page === 1 &&
        !search &&
        selectedCompetency?.code &&
        !options.some((o) => o.id === selectedCompetency.code)
      ) {
        options.unshift({
          id: selectedCompetency.code,
          name: `${selectedCompetency.code} - ${selectedCompetency.name}`,
        });
      }
      return { data: options, total_count: res.total_count || 0 };
    });

  /* ── dependency sets: sets from competencies that the selected competency depends on.
     An mcq set may only depend on a grid set (mobile's "smart set selection" reads a
     min grid score off the grid set's questions), so non-grid sets are excluded here. ── */
  const dependentCompetencyCodes = selectedCompetency?.depends_on || [];
  const availableDependencySets = setForm
    ? allSetsLite.filter((s) =>
        dependentCompetencyCodes.includes(s.competency_code) &&
        String(s.competency_state_id) === String(setForm.state_id) &&
        s.id !== editingSetId && s.set_type === "grid")
    : [];

  /* ── one set type per competency: the type the competency's other sets already use ── */
  const competencyTypes = setForm?.competency_code
    ? new Set(allSetsLite
        .filter((s) => s.competency_code === setForm.competency_code &&
          String(s.competency_state_id) === String(setForm.state_id) && s.id !== editingSetId)
        .map((s) => s.set_type || "mcq"))
    : new Set();
  const lockedSetType = competencyTypes.size === 1 ? [...competencyTypes][0] : null;

  /* ── bulk upload handlers ── */
  const openUploadModal = (mode) => {
    setUploadMode(mode);
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadModal(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && !f.name.endsWith(".xlsx")) {
      setToast({ type: "error", message: "Only .xlsx files are accepted" });
      return;
    }
    setUploadFile(f || null);
    setUploadResult(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && !f.name.endsWith(".xlsx")) {
      setToast({ type: "error", message: "Only .xlsx files are accepted" });
      return;
    }
    setUploadFile(f || null);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!uploadFile || uploadLoading) return;
    setUploadLoading(true);
    setUploadResult(null);
    try {
      const res = await UPLOAD_MODES[uploadMode].apiFn(uploadFile);
      setUploadResult(res);
      if (res.success) {
        setToast({ type: "success", message: res.message || `Inserted ${res.inserted} set(s)` });
        loadSets();
        loadAllSetsLite();
        setShowUploadModal(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed";
      setUploadResult({ success: false, errors: [], message: typeof detail === "string" ? detail : JSON.stringify(detail) });
      setToast({ type: "error", message: typeof detail === "string" ? detail : "Upload failed" });
    } finally {
      setUploadLoading(false);
    }
  };

  /* ═════════ RENDER ═════════ */
  // Stop Rule's "stop after N consecutive wrong answers" can't exceed how
  // many items are actually in the set — grid_items for a grid set,
  // formQuestions for an MCQ set. Falls back to 20 before any items exist.
  const stopRuleMaxItems = setForm?.set_type === "grid"
    ? (setForm.grid_items?.length || 20)
    : (formQuestions.length || 20);
  return (
    <div className="p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ═══ FORM VIEW ═══ */}
      {view === "form" && setForm && (
        <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg border border-[#e5e7eb]">
          {/* Form Header */}
          <div className="sticky top-14 z-40 flex items-center justify-between gap-3 px-6 py-4 border-b border-[#e5e7eb] bg-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <button onClick={() => {
                setView("list");
                setSetForm(null);
                setFormQuestions([]);
              }}
                className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
                title="Back"
              >
                <HiOutlineChevronLeft className="text-lg" />
              </button>
              <div>
                <h2 className="m-0 text-[15px] font-semibold text-[#24292f]">
                  {!canManageCompetencies ? "Set Details" : editingSetId ? "Edit Set" : "Add New Set"}
                </h2>
                <p className="m-0 text-[13px] text-[#8b949e]">
                  {!canManageCompetencies
                    ? "View set information"
                    : editingSetId
                    ? "Update set info, grid, questions — all saved together"
                    : "Create a new assessment set with questions"}
                </p>
              </div>
            </div>
            {canManageCompetencies && editingSetId && (
              <button
                onClick={() => setDeleteConfirm({ type: "set", item: { id: editingSetId, title: setForm.title } })}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>

          <div className="p-6 space-y-5">

            {/* ── Set Type Selector ── */}
            <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
              <legend className="text-xs font-semibold text-[#57606a] px-2">Set Type</legend>
              <div className="grid grid-cols-2 gap-3">
                {SET_TYPES.map((t) => {
                  const typeBlocked = !!lockedSetType && lockedSetType !== t.value;
                  return (
                  <button key={t.value} type="button"
                    onClick={() => updateSetForm({ set_type: t.value, ...(t.value !== "mcq" && { dependent_set_id: null }) })}
                    disabled={!canManageCompetencies || typeBlocked}
                    className={"p-3 rounded-lg border-2 text-left transition-all " +
                      (canManageCompetencies && !typeBlocked ? "cursor-pointer " : "cursor-not-allowed opacity-50 ") +
                      (setForm.set_type === t.value
                        ? (t.color === "blue" ? "border-blue-500 bg-blue-50" : t.color === "emerald" ? "border-emerald-500 bg-emerald-50" : "border-purple-500 bg-purple-50")
                        : "border-[#e5e7eb] bg-white hover:border-[#d0d7de]")}
                  >
                    <div className="text-sm font-semibold text-[#24292f]">{t.label}</div>
                    <div className="text-[10px] text-[#57606a] mt-0.5">{t.desc}</div>
                  </button>
                  );
                })}
              </div>
              {lockedSetType && (
                <p className="text-[11px] text-[#57606a] mt-2">
                  This competency already has {lockedSetType === "grid" ? "Grid" : "MCQ"} sets, so all its sets must be {lockedSetType === "grid" ? "Grid" : "MCQ"}.
                </p>
              )}
            </fieldset>

            {/* ── Basic Info ── */}
            <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
              <legend className="text-xs font-semibold text-[#57606a] px-2">Basic Info</legend>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>State <span className="text-red-500">*</span></label>
                  <SearchableDropdown
                    /* Remount once the options land so a preselected state resolves
                       its label (SearchableDropdown only resolves it while the label
                       is still empty). Same trick as the other static callers. */
                    key={states.length ? "states-loaded" : "states-loading"}
                    value={setForm.state_id || ""}
                    onChange={(id) => {
                      /* A competency belongs to exactly one state, so switching state
                         invalidates the current pick and anything derived from it. */
                      updateSetForm(
                        String(id) !== String(setForm.state_id || "")
                          ? { state_id: id, competency_code: "", dependent_set_id: null }
                          : { state_id: id }
                      );
                    }}
                    placeholder={states.length ? "Select a state" : "Loading states..."}
                    fetchFn={scopedStatesFetchFn(role === "ADMIN", states)}
                    resolveFn={(id) => fetchStateById(id).then((res) => res.data)}
                    cacheNamespace="set_state_id"
                    disabled={states.length <= 1 || !canManageCompetencies}
                    variant="form"
                  />
                  <p className="text-[10px] text-[#8b949e] mt-1">
                    {states.length === 1
                      ? "Your assigned state — competencies below are limited to it."
                      : "Pick a state to list its competencies."}
                  </p>
                </div>
                <div>
                  <label className={labelClass}>Competency <span className="text-red-500">*</span></label>
                  {setForm.state_id ? (
                    <SearchableDropdown
                      /* Keyed on both so the list refetches when the state changes and
                         the label resolves once the competency itself is resolved. */
                      key={`${setForm.state_id}-${selectedCompetency?.code || "unresolved"}`}
                      value={setForm.competency_code || ""}
                      onChange={(code) => {
                        /* Dependency sets come from the competency's depends_on, so a
                           previously chosen one is usually invalid under a new
                           competency — clear it rather than silently saving a
                           cross-competency link. Only on a real change, and only from
                           this handler, so a slow allSetsLite load can never wipe a
                           valid saved value. */
                        if (code === setForm.competency_code) {
                          updateSetForm({ competency_code: code });
                          return;
                        }
                        // A new set takes the type the competency's sets already use.
                        const types = new Set(allSetsLite
                          .filter((s) => s.competency_code === code &&
                            String(s.competency_state_id) === String(setForm.state_id) && s.id !== editingSetId)
                          .map((s) => s.set_type || "mcq"));
                        const only = types.size === 1 ? [...types][0] : null;
                        updateSetForm({
                          competency_code: code,
                          dependent_set_id: null,
                          ...(!editingSetId && only && { set_type: only }),
                        });
                      }}
                      placeholder="Search competency by code or name..."
                      fetchFn={competencyFetchFn}
                      variant="form"
                      emptyMessage="No competencies in this state"
                      disabled={!canManageCompetencies}
                    />
                  ) : (
                    /* No state yet. Show the existing competency read-only rather than
                       a disabled dropdown, whose label resolution is skipped when
                       disabled and would render an empty box. */
                    <input
                      type="text"
                      value={selectedCompetency
                        ? `${selectedCompetency.code} - ${selectedCompetency.name}`
                        : setForm.competency_code || ""}
                      className={inputClass + " bg-gray-50 cursor-not-allowed"}
                      placeholder="Select a state first"
                      disabled
                      readOnly
                    />
                  )}
                </div>
                <div>
                  <label className={labelClass}>Title <span className="text-red-500">*</span></label>
                  <input type="text" value={setForm.title}
                    onChange={(e) => updateSetForm({ title: e.target.value })}
                    className={inputClass} placeholder="Set title" autoFocus
                    disabled={!canManageCompetencies}
                  />
                </div>
                <div>
                  <label className={labelClass}>Description</label>
                  <textarea rows={2} value={setForm.description}
                    onChange={(e) => updateSetForm({ description: e.target.value })}
                    className={inputClass} placeholder="Optional description"
                    disabled={!canManageCompetencies}
                  />
                </div>
              </div>
            </fieldset>

            {/* ── Dependency Set (link to a set from a dependent competency) ── */}
            {hasMcq && setForm.competency_code && availableDependencySets.length > 0 && (
              <fieldset className="border border-purple-200 rounded-lg p-4 m-0 bg-purple-50/30">
                <legend className="text-xs font-semibold text-purple-700 px-2">Dependency Set</legend>
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Depends on Set</label>
                    <select
                      value={setForm.dependent_set_id || ""}
                      onChange={(e) => updateSetForm({ dependent_set_id: e.target.value || null })}
                      className={selectClass}
                      disabled={!canManageCompetencies}
                    >
                      <option value="">No dependency (standalone)</option>
                      {availableDependencySets.map((ds) => (
                        <option key={ds.id} value={ds.id}>
                          {ds.title}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-[#8b949e] mt-1">
                      Link this set to a set from a dependent competency — used for smart set selection on mobile.
                    </p>
                  </div>
                </div>
              </fieldset>
            )}

            {/* ── Instructions ── */}
            <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
              <legend className="text-xs font-semibold text-[#57606a] px-2">Instructions</legend>
              <div className={!canManageCompetencies ? "pointer-events-none opacity-70" : ""}>
                <TiptapEditor
                  value={setForm.instructions}
                  onChange={(text) => updateSetForm({ instructions: text })}
                  placeholder="Enter instructions — select text and pick a colour to highlight..."
                  minHeight="260px"
                  enableColors
                />
              </div>
            </fieldset>



            {/* ── Timer (only for grid) ── */}
            {hasGrid && (
            <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
              <legend className="text-xs font-semibold text-[#57606a] px-2">Timer</legend>
              <div className="flex items-center gap-4">
                <button type="button"
                  onClick={() => updateSetForm({ is_timed: !setForm.is_timed })}
                  disabled={!canManageCompetencies}
                  className={"flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer disabled:cursor-not-allowed " +
                    (setForm.is_timed ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-[#d0d7de] text-[#57606a]")}
                >
                  <HiOutlineClock className="text-sm" />
                  {setForm.is_timed ? "Timed ✓" : "Not Timed"}
                </button>
                {setForm.is_timed && (
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} value={setForm.duration_secs || ""}
                      onChange={(e) => updateSetForm({ duration_secs: parseInt(e.target.value) || null })}
                      onKeyDown={blockNonNumericKeys}
                      className={inputClass + " w-24"} placeholder="secs *"
                      title="Required when Timed is enabled"
                      disabled={!canManageCompetencies}
                    />
                    <span className="text-xs text-[#57606a]">seconds</span>
                  </div>
                )}
              </div>
            </fieldset>
            )}

            {/* ── Stop Rule ── */}
            <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
              <legend className="text-xs font-semibold text-[#57606a] px-2">Stop Rule</legend>
              <div className="flex items-center gap-4">
                <button type="button"
                  onClick={() => updateSetForm({ stop_rule_enabled: !setForm.stop_rule_enabled })}
                  disabled={!canManageCompetencies}
                  className={"flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer disabled:cursor-not-allowed " +
                    (setForm.stop_rule_enabled ? "bg-red-50 border-red-300 text-red-700" : "bg-white border-[#d0d7de] text-[#57606a]")}
                >
                  {setForm.stop_rule_enabled ? "Stop Rule ON ✓" : "Stop Rule OFF"}
                </button>
                {setForm.stop_rule_enabled && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#57606a]">Stop after</span>
                    <input type="number" min={1} max={stopRuleMaxItems} value={setForm.stop_if_consec || ""}
                      onChange={(e) => updateSetForm({ stop_if_consec: clampNumber(parseInt(e.target.value, 10), 1, stopRuleMaxItems) })}
                      onKeyDown={blockNonNumericKeys}
                      className={inputClass + " w-20"} placeholder="N *"
                      title="Required when Stop Rule is enabled"
                      disabled={!canManageCompetencies}
                    />
                    <span className="text-xs text-[#57606a]">consecutive wrong answers</span>
                  </div>
                )}
              </div>
            </fieldset>

            {/* ── Thresholds ── */}
            <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
              <legend className="text-xs font-semibold text-[#57606a] px-2">Thresholds (Passing Criteria)</legend>
              <div className="space-y-3">
                <button type="button"
                  onClick={() => updateSetForm({ thresholds_enabled: !setForm.thresholds_enabled })}
                  disabled={!canManageCompetencies}
                  className={"flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer disabled:cursor-not-allowed " +
                    (setForm.thresholds_enabled ? "bg-green-50 border-green-300 text-green-700" : "bg-white border-[#d0d7de] text-[#57606a]")}
                >
                  {setForm.thresholds_enabled ? "Thresholds ON ✓" : "Thresholds OFF"}
                </button>
                {setForm.thresholds_enabled && (
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    {setForm.set_type === "mcq" && (
                      <div>
                        <label className={labelClass}>Passing Percentage (MCQ) <span className="text-red-500">*</span></label>
                        <div className="flex items-center gap-2">
                          <input type="number" min={0} max={100} value={setForm.passing_percentage || ""}
                            onChange={(e) => updateSetForm({ passing_percentage: clampNumber(parseFloat(e.target.value), 0, 100) })}
                            onKeyDown={blockNonNumericKeys}
                            className={inputClass + " w-24"} placeholder="70 *"
                            title="Required when Thresholds is enabled"
                            disabled={!canManageCompetencies}
                          />
                          <span className="text-xs text-[#57606a]">%</span>
                        </div>
                      </div>
                    )}
                    {setForm.set_type === "grid" && (
                      <div>
                        <label className={labelClass}>Passing Score (Grid) <span className="text-red-500">*</span></label>
                        <div className="flex items-center gap-2">
                          <input type="number" min={0} value={setForm.passing_score || ""}
                            onChange={(e) => updateSetForm({ passing_score: parseInt(e.target.value) || null })}
                            onKeyDown={blockNonNumericKeys}
                            className={inputClass + " w-24"} placeholder="5 *"
                            title="Required when Thresholds is enabled"
                            disabled={!canManageCompetencies}
                          />
                          <span className="text-xs text-[#57606a]">items read correctly</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </fieldset>

            {/* ── Grid Builder (for grid sets) ── */}
            {hasGrid && (
              <fieldset className="border border-emerald-200 rounded-lg p-4 m-0 bg-emerald-50/30">
                <legend className="text-xs font-semibold text-emerald-700 px-2">Grid Builder</legend>
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Grid Text (paste the passage / word list)</label>
                    <textarea rows={4} value={setForm.grid_text}
                      onChange={(e) => handleGridTextChange(e.target.value)}
                      className={inputClass} placeholder="Paste the reading passage here — each word becomes a grid item."
                      disabled={!canManageCompetencies}
                    />
                  </div>
                  {setForm.grid_items && setForm.grid_items.length > 0 && (
                    <div>
                      <label className={labelClass}>Grid Preview ({setForm.grid_items.length} items)</label>
                      <div className="border border-[#d0d7de] rounded-lg p-3 bg-white overflow-x-auto">
                        <div className="flex flex-wrap gap-1">
                          {setForm.grid_items.map((item, i) => (
                            <div key={i} className="px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-xs font-mono">
                              {item.text}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>
            )}

            {/* ── Feedback MCQ (optional, for grid sets) ── */}
            {hasGrid && (
              <fieldset className="border border-purple-200 rounded-lg p-4 m-0 bg-purple-50/30">
                <legend className="text-xs font-semibold text-purple-700 px-2">Feedback Question (Optional)</legend>
                <p className="text-[11px] text-[#8b949e] mb-3">Add an optional MCQ question shown after the grid assessment for student feedback.</p>
                {!setForm.feedback_question ? (
                  canManageCompetencies && (
                    <button type="button" onClick={() => updateSetForm({ feedback_question: { text: "", options: [{ text: "", is_correct: false }, { text: "", is_correct: false }] } })}
                      className="text-xs font-medium text-purple-600 border border-purple-300 rounded-lg px-3 py-1.5 hover:bg-purple-100 transition-colors cursor-pointer bg-white">
                      + Add Feedback Question
                    </button>
                  )
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[11px] font-medium text-[#57606a] mb-1">Question Text</label>
                      <input type="text" value={setForm.feedback_question.text}
                        onChange={e => updateSetForm({ feedback_question: { ...setForm.feedback_question, text: e.target.value } })}
                        className="w-full h-9 px-3 border border-[#d0d7de] rounded-lg text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                        placeholder="e.g. How confident did the student feel while reading?"
                        disabled={!canManageCompetencies} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-[#57606a] mb-1">Options</label>
                      <div className="space-y-2">
                        {setForm.feedback_question.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <input type="text" value={opt.text}
                              onChange={e => {
                                const opts = [...setForm.feedback_question.options];
                                opts[oi] = { ...opts[oi], text: e.target.value };
                                updateSetForm({ feedback_question: { ...setForm.feedback_question, options: opts } });
                              }}
                              className="flex-1 h-8 px-2.5 border border-[#d0d7de] rounded-md text-xs focus:outline-none focus:border-purple-400"
                              placeholder={`Option ${oi + 1}`}
                              disabled={!canManageCompetencies} />
                            {canManageCompetencies && (
                              <button type="button" onClick={() => {
                                const opts = setForm.feedback_question.options.filter((_, i) => i !== oi);
                                updateSetForm({ feedback_question: { ...setForm.feedback_question, options: opts } });
                              }} className="text-red-400 hover:text-red-600 text-xs cursor-pointer border-none bg-transparent" disabled={setForm.feedback_question.options.length <= 2}>
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {canManageCompetencies && (
                        <button type="button" onClick={() => {
                          const opts = [...setForm.feedback_question.options, { text: "", is_correct: false }];
                          updateSetForm({ feedback_question: { ...setForm.feedback_question, options: opts } });
                        }} className="mt-2 text-[11px] text-purple-600 font-medium cursor-pointer border-none bg-transparent hover:underline">
                          + Add Option
                        </button>
                      )}
                    </div>
                    {canManageCompetencies && (
                      <button type="button" onClick={() => updateSetForm({ feedback_question: null })}
                        className="text-[11px] text-red-500 font-medium cursor-pointer border-none bg-transparent hover:underline">
                        Remove Feedback Question
                      </button>
                    )}
                  </div>
                )}
              </fieldset>
            )}

            {/* ── Set Resources (this uploader only adds images; sets bulk-uploaded
                 with a PDF in resource_urls can still have a document here) ── */}
            <fieldset className="border border-[#e5e7eb] rounded-lg p-4 m-0">
              <legend className="text-xs font-semibold text-[#57606a] px-2">Set Resources</legend>
              {(setForm.resources || []).length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {setForm.resources.map((res, idx) => (
                    <div key={idx} className="relative group">
                      {res.file_type === "image" ? (
                        <img src={res.url} alt={res.label || `Image ${idx + 1}`} className="h-20 w-20 object-cover rounded-lg border border-[#d0d7de]" />
                      ) : (
                        <a href={res.url} target="_blank" rel="noreferrer"
                          className="h-20 w-20 flex flex-col items-center justify-center gap-1 rounded-lg border border-[#d0d7de] hover:bg-[#f6f8fa] transition-colors no-underline"
                        >
                          <HiOutlineDocumentText className="text-2xl text-[#8b949e]" />
                        </a>
                      )}
                      {canManageCompetencies && (
                        <button type="button" onClick={() => removeResource(idx)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer border-none shadow">
                          <HiOutlineX className="text-[10px]" />
                        </button>
                      )}
                      {res.label && <p className="text-[10px] text-[#8b949e] text-center mt-1 truncate max-w-[80px]">{res.label}</p>}
                    </div>
                  ))}
                </div>
              )}
              {canManageCompetencies && (
                <div className="flex items-center gap-2">
                  <input type="file" id="set-image-upload" className="hidden" accept="image/*"
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleResourceUpload(file); e.target.value = ""; }}
                  />
                  <label htmlFor="set-image-upload"
                    className={"text-xs px-3 py-2 border border-[#d0d7de] rounded-lg cursor-pointer hover:bg-[#f6f8fa] transition-all flex items-center gap-1 " + (resourceUploading ? "opacity-50 pointer-events-none" : "")}
                  >
                    <HiOutlinePhotograph className="text-sm" />
                    {resourceUploading ? "Uploading..." : "Upload Image"}
                  </label>
                </div>
              )}
            </fieldset>

            {/* ── Inline Questions (for mcq sets) ── */}
            {hasMcq && (
              <fieldset className="border border-blue-200 rounded-lg p-4 m-0 bg-blue-50/30">
                <legend className="text-xs font-semibold text-blue-700 px-2">
                  Questions ({formQuestions.length})
                  {setForm.dependent_set_id && <span className="font-normal text-[#57606a] ml-1">— Dependent: each question can require a minimum grid score</span>}
                </legend>

                {/* Question list */}
                <div className="space-y-2 mb-3">
                  {formQuestions.map((q, qIdx) => (
                    <div key={q._key || qIdx} className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
                      {/* Question header (collapsible) */}
                      <div className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-[#f6f8fa] transition-colors"
                        onClick={() => setExpandedQuestionIdx(expandedQuestionIdx === qIdx ? null : qIdx)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] text-[#8b949e] font-medium">#{qIdx + 1}</span>
                          <span className="text-sm text-[#24292f] truncate">{q.text || "(untitled question)"}</span>
                          {setForm.dependent_set_id && q.min_grid_score_required > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded flex-shrink-0">
                              Grid ≥ {q.min_grid_score_required}
                            </span>
                          )}
                          {q.options && (
                            <span className="text-[10px] text-[#8b949e] flex-shrink-0">{q.options.length} opts</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {canManageCompetencies && (
                            <button onClick={(e) => { e.stopPropagation(); removeQuestion(qIdx); }}
                              className="p-1 text-red-400 hover:text-red-600 rounded hover:bg-red-50 transition-all bg-transparent border-none cursor-pointer"
                              title="Remove question"
                            >
                              <HiOutlineTrash className="text-sm" />
                            </button>
                          )}
                          {expandedQuestionIdx === qIdx ? <HiOutlineChevronUp className="text-sm text-[#8b949e]" /> : <HiOutlineChevronDown className="text-sm text-[#8b949e]" />}
                        </div>
                      </div>

                      {/* Expanded question editor */}
                      {expandedQuestionIdx === qIdx && (
                        <div className="border-t border-[#e5e7eb] p-3 space-y-3">
                          <div>
                            <label className={labelClass}>Question Text <span className="text-red-500">*</span></label>
                            <textarea rows={2} value={q.text}
                              onChange={(e) => updateQuestion_local(qIdx, "text", e.target.value)}
                              className={inputClass} placeholder="Enter question / prompt text..."
                              disabled={!canManageCompetencies}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Answer <span className="text-gray-500 text-xs">(Answer description)</span></label>
                            <input type="text" value={q.answer || ""}
                              onChange={(e) => updateQuestion_local(qIdx, "answer", e.target.value)}
                              className={inputClass} placeholder="Enter the correct answer"
                              disabled={!canManageCompetencies}
                            />
                          </div>

                          {/* Question Image Upload */}
                          <div>
                            <label className={labelClass}>Question Image <span className="text-gray-500 text-xs">(optional)</span></label>
                            <div className="flex items-center gap-2">
                              {canManageCompetencies && (
                                <>
                                  <input type="file" accept="image/*" id={`q-img-${qIdx}`} className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      try {
                                        const data = await uploadSetResource(file);
                                        updateQuestion_local(qIdx, "image_url", data.url);
                                        setToast({ type: "success", message: "Image uploaded" });
                                      } catch {
                                        setToast({ type: "error", message: "Image upload failed" });
                                      }
                                    }}
                                  />
                                  <label htmlFor={`q-img-${qIdx}`}
                                    className="px-3 py-2 text-xs border border-[#d0d7de] rounded-lg cursor-pointer hover:bg-[#f6f8fa] transition-all flex items-center gap-1">
                                    📷 {q.image_url ? "Change Image" : "Upload Image"}
                                  </label>
                                </>
                              )}
                              {q.image_url && (
                                <>
                                  <img src={q.image_url} alt="question" className="h-10 w-10 object-cover rounded border" />
                                  {canManageCompetencies && (
                                    <button type="button" onClick={() => updateQuestion_local(qIdx, "image_url", "")}
                                      className="text-xs text-red-500 hover:underline">Remove</button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          {setForm.dependent_set_id && (() => {
                            // sets is only the current paginated page — the dependent
                            // set (often created earlier) may not be on it. allSetsLite
                            // is the full unpaginated list, so look there instead.
                            const depSet = allSetsLite.find((s) => s.id === setForm.dependent_set_id);
                            return depSet && depSet.set_type === "grid";
                          })() && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                              <label className="block text-xs font-semibold text-purple-800 mb-1">Min Grid Score Required</label>
                              <input type="number" min={0} value={q.min_grid_score_required || ""}
                                onChange={(e) => updateQuestion_local(qIdx, "min_grid_score_required", parseInt(e.target.value) || null)}
                                onKeyDown={blockNonNumericKeys}
                                className={inputClass + " w-24"} placeholder="0"
                                disabled={!canManageCompetencies}
                              />
                              <p className="text-[10px] text-purple-600 mt-1">
                                Student must have read at least this many grid items in the dependent grid set for this question to appear.
                              </p>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className={labelClass + " mb-0"}>Options</label>
                              {canManageCompetencies && (
                                <button type="button" onClick={() => addOption(qIdx)} className="text-xs text-[#0078d4] hover:underline">+ Add Option</button>
                              )}
                            </div>
                            {q.options.map((opt, oIdx) => (
                              <div key={oIdx} className="flex items-center gap-2 mb-2">
                                <input type="checkbox" checked={opt.is_correct}
                                  onChange={(e) => updateOption(qIdx, oIdx, "is_correct", e.target.checked)}
                                  className="rounded border-[#d0d7de]" title="Mark as correct"
                                  disabled={!canManageCompetencies}
                                />
                                <input type="text" value={opt.text}
                                  onChange={(e) => updateOption(qIdx, oIdx, "text", e.target.value)}
                                  className={inputClass + " flex-1"} placeholder={"Option " + (oIdx + 1)}
                                  disabled={!canManageCompetencies}
                                />
                                <span className={"text-[10px] px-1.5 py-0.5 rounded " + (opt.is_correct ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600")}>
                                  {opt.is_correct ? "Correct" : "Wrong"}
                                </span>
                                {canManageCompetencies && q.options.length > 2 && (
                                  <button onClick={() => removeOption(qIdx, oIdx)} className="p-1 text-red-400 hover:text-red-600 bg-transparent border-none cursor-pointer">
                                    <HiOutlineX className="text-xs" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {canManageCompetencies && (
                  <button type="button" onClick={addQuestion}
                    className="text-xs px-3 py-1.5 bg-[#0078d4] text-white rounded-md hover:bg-[#106ebe] transition-all flex items-center gap-1 cursor-pointer border-none"
                  >
                    <HiOutlinePlus className="text-sm" /> Add Question
                  </button>
                )}
              </fieldset>
            )}

            {/* Save / Cancel */}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" className={btnSecondary}
                onClick={() => {
                  setView("list");
                  setSetForm(null);
                  setFormQuestions([]);
                }}
              >{canManageCompetencies ? "Cancel" : "Back"}</button>
              {canManageCompetencies && (
                <button onClick={handleSetSave} disabled={saving} className={btnPrimary + " disabled:opacity-50"}>
                  {saving ? "Saving..." : editingSetId ? "Save Changes" : "Create Set"}
                </button>
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {/* ═══ LIST VIEW ═══ */}
      {view === "list" && (
      <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
          <div className="relative flex items-center flex-1 max-w-sm">
            <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
            <input type="text"
              className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
              placeholder="Search sets..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6]"
                onClick={() => setSearchTerm("")}
              ><HiOutlineX className="text-sm" /></button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <FilterDropdown
              filters={[
                {
                  key: "state_id",
                  label: "State",
                  type: "select",
                  options: states.map((s) => ({ value: String(s.id), label: s.name })),
                },
                {
                  key: "competency_code",
                  label: "Competency",
                  type: "select",
                  options: (
                    draftListFilters.state_id
                      ? (filterCompetenciesByState[draftListFilters.state_id] || [])
                      : (allCompetenciesForFilter || [])
                  ).map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` })),
                },
                {
                  key: "set_type",
                  label: "Set Type",
                  type: "select",
                  options: SET_TYPES.map((t) => ({ value: t.value, label: t.label })),
                },
              ]}
              values={listFilters}
              onChange={(newFilters) => {
                // Switching state invalidates any competency picked under the old one.
                const cleaned = newFilters.state_id !== draftListFilters.state_id
                  ? { ...newFilters, competency_code: "" }
                  : newFilters;
                setListFilters(cleaned);
                setDraftListFilters(cleaned);
                setCurrentPage(1);
              }}
              onDraftChange={(draft) => {
                const cleaned = draft.state_id !== draftListFilters.state_id
                  ? { ...draft, competency_code: "" }
                  : draft;
                setDraftListFilters(cleaned);
              }}
              onOpen={() => setFilterPanelOpened(true)}
            />
            {canManageCompetencies && (
              <>
                <button className="inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]"
                  onClick={() => openUploadModal("mcq")}
                ><HiOutlineUpload className="text-base" /> Upload MCQ-Set</button>
                <button className="inline-flex items-center gap-2 h-10 px-4 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]"
                  onClick={() => openUploadModal("grid")}
                ><HiOutlineUpload className="text-base" /> Upload Grid-Set</button>
                <button className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
                  onClick={openSetCreate}
                ><HiOutlinePlus className="text-base" /> Add Set</button>
              </>
            )}
          </div>
        </div>

        {setsLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-5 h-5 bg-[#e5e7eb] rounded" />
                <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
                <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
              </div>
            ))}
          </div>
        ) : sets.length === 0 ? (
          <div className="text-center py-16 px-5">
            <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
              <HiOutlineClipboardList className="text-2xl text-[#8b949e]" />
            </div>
            <p className="m-0 text-sm font-medium text-[#57606a]">
              {searchTerm ? "No sets match your search." : "No sets found."}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[40px]">#</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a]">Title</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[80px]">Type</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[120px]">Competency</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[100px]">Stop Rule</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[120px]">Dependent Set</th>
                <th className="text-left py-3.5 px-6 text-xs font-medium text-[#57606a] w-[100px]">Threshold</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e7eb]">
              {sets.map((s, idx) => {
                const typeInfo = SET_TYPES.find((t) => t.value === s.set_type) || SET_TYPES[0];
                return (
                  <tr
                    key={s.id}
                    onClick={() => openSetEdit(s)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="py-4 px-6 text-[#8b949e] font-medium text-xs">{(currentPage - 1) * pageSize + idx + 1}</td>
                    <td className="py-4 px-6">
                      <span className="font-medium text-[#24292f]">{s.title}</span>
                      {s.resources && s.resources.length > 0 && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{s.resources.length} res</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <span className={"text-[10px] px-1.5 py-0.5 rounded font-semibold " +
                        (s.set_type === "mcq" ? "bg-blue-50 text-blue-700" :
                         s.set_type === "grid" ? "bg-emerald-50 text-emerald-700" :
                         "bg-purple-50 text-purple-700")}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {s.competency_code ? (
                        <code className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-[#0078d4] rounded font-mono">{s.competency_code}</code>
                      ) : (
                        <span className="text-xs text-[#8b949e]">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      {s.stop_rule_enabled ? (
                        <span className="text-xs text-[#24292f]">{s.stop_if_consec} consecutive</span>
                      ) : (
                        <span className="text-xs text-[#8b949e]">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      {s.dependent_set_id ? (() => {
                        const depSet = allSetsLite.find(ds => ds.id === s.dependent_set_id);
                        return (
                          <span className="text-xs text-[#24292f] truncate block max-w-[140px]" title={depSet ? depSet.title : s.dependent_set_id}>
                            {depSet ? depSet.title : "Unknown"}
                            {s.show_after_grid_item ? ` (after item ${s.show_after_grid_item})` : ""}
                          </span>
                        );
                      })() : (
                        <span className="text-xs text-[#8b949e]">—</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      {s.thresholds_enabled ? (
                        <span className="text-xs text-[#24292f]">
                          {s.set_type === "mcq" && s.passing_percentage ? `${s.passing_percentage}%` : ""}
                          {s.set_type === "grid" && s.passing_score ? `${s.passing_score}` : ""}
                          {s.set_type !== "mcq" && s.set_type !== "grid" && s.passing_percentage ? `${s.passing_percentage}%` : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-[#8b949e]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!setsLoading && totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={(p) => loadSets(p, debouncedSearchTerm, listFilters)}
            onPageSizeChange={handlePageSizeChange}
            entityName="sets"
          />
        )}
      </div>
      )}

      {/* ═══ DELETE CONFIRM MODAL ═══ */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
            role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <HiOutlineExclamation className="text-2xl text-red-600" />
              </div>
              <h3 className="m-0 text-lg font-semibold text-[#24292f]">Delete Set?</h3>
              <p className="m-0 mt-2 text-sm text-[#57606a]">
                This will delete "{deleteConfirm.item.title}" and all its questions.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                onClick={() => setDeleteConfirm(null)}
              >Cancel</button>
              <button className="flex-1 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all bg-red-600 text-white hover:bg-red-700"
                onClick={() => handleSetDelete(deleteConfirm.item.id)}
              >Confirm Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ BULK UPLOAD MODAL ═══ */}
      {showUploadModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
          onClick={() => !uploadLoading && setShowUploadModal(false)}
        >
          <div
            className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape' && !uploadLoading) setShowUploadModal(false); }}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
              <div>
                <h3 className="m-0 text-base font-semibold text-[#24292f]">{UPLOAD_MODES[uploadMode].title}</h3>
                <p className="m-0 mt-0.5 text-xs text-[#8b949e]">{UPLOAD_MODES[uploadMode].hint}</p>
              </div>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
                onClick={() => setShowUploadModal(false)}
                disabled={uploadLoading}
              >
                <HiOutlineX className="text-lg" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Template Download */}
              <div className="flex items-center gap-3 p-3 bg-[#eff6ff] rounded-md border border-[#d0d5dd]">
                <HiOutlineDocumentText className="text-lg text-[#0078d4] flex-shrink-0" />
                <div className="flex-1">
                  <p className="m-0 text-sm font-medium text-[#24292f]">Need a template?</p>
                  <p className="m-0 text-xs text-[#57606a] mt-0.5">{UPLOAD_MODES[uploadMode].hint}</p>
                </div>
                <button
                  className="inline-flex items-center gap-1 py-1.5 px-3 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#0078d4] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                  onClick={() => UPLOAD_MODES[uploadMode].download()}
                >
                  <HiOutlineDownload className="text-sm" />
                  Download
                </button>
              </div>

              {/* File Input */}
              <div>
                <label className="block mb-2 text-sm font-medium text-[#24292f]">Excel File (.xlsx)</label>
                <div
                  className={`relative border-2 border-dashed rounded-md p-6 text-center transition-colors cursor-pointer ${isDragging ? "border-[#0078d4] bg-[#eff6ff]" : "border-[#d0d5dd] hover:border-[#0078d4]"}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <HiOutlineUpload className="text-2xl text-[#8b949e] mx-auto mb-2" />
                  {uploadFile ? (
                    <p className="m-0 text-sm font-medium text-[#24292f]">{uploadFile.name}</p>
                  ) : (
                    <p className="m-0 text-sm text-[#8b949e]">Click to select or drag a file</p>
                  )}
                  <p className="m-0 text-xs text-[#8b949e] mt-1">Max 5MB</p>
                </div>
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className={`p-4 rounded-xl border ${
                  uploadResult.success
                    ? "bg-[#f0fdf4] border-[#bbf7d0]"
                    : "bg-red-50 border-red-200"
                }`}>
                  <p className={`m-0 text-sm font-semibold ${
                    uploadResult.success ? "text-[#16a34a]" : "text-red-700"
                  }`}>
                    {uploadResult.success
                      ? `Inserted ${uploadResult.inserted} record(s)${uploadResult.skipped ? ` · Skipped ${uploadResult.skipped}` : ""}`
                      : uploadResult.message || "Validation failed"}
                  </p>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
                      {uploadResult.errors.map((err, i) => (
                        <p key={i} className="m-0 text-xs text-red-600">
                          Row {err.row}: {err.message}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button
                className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50"
                onClick={() => setShowUploadModal(false)}
                disabled={uploadLoading}
              >
                {uploadResult?.success ? "Close" : "Cancel"}
              </button>
              {!uploadResult?.success && (
                <button
                  className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploadLoading}
                >
                  {uploadLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Uploading…
                    </>
                  ) : (
                    <>
                      <HiOutlineUpload className="text-base" />
                      Upload
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sets;
